import { action, makeObservable, observable } from 'mobx';
import { SingleUserType } from 'types/GlobalTypes';
import { UserData } from 'types/models/UserData';
import { INotificationsStore } from './NotificationsStore';
import { IOrganizationsStore } from './OrganizationsStore';
import { IUsersService } from 'services/UsersService';
import { getErrorMessage, storeResponseHandler } from 'utilities/ErrorHelper';
import { BaseStoreClass, IBaseStoreClass } from './BaseStoreClass';
import { t } from 'i18next';
import { UserDataAndOrganization } from 'types/models/OrganizationModels';

export interface IUsersStore extends IBaseStoreClass {
  users: SingleUserType[];
  selectedUser: SingleUserType | null;
  userRoles: string[];
  isLoadingUsers: boolean;
  isRevokingAccess: boolean;
  isGrantingAccess: boolean;
  isSearchingForNewUser: boolean;
  usersLoadError: boolean;
  isOrgNotSynced: boolean;
  setSelectedUser(user: SingleUserType | null): void;
  getUsers(orgid: string): void;
  findNewUserByEmail(email: string): Promise<UserData[] | null>;
  RevokeUser(content: IRevokeAccess): void;
}

export interface IGrantAccess {
  userId: string;
  organizationId: string;
  type: string;
}

export interface IRevokeAccess {
  userId: string;
  organizationId: string;
}

export class UsersStore extends BaseStoreClass implements IUsersStore {
  users: SingleUserType[] = [];
  selectedUser: SingleUserType | null = null;
  userRoles: string[] = ['Admin', 'Coach'];

  isLoadingUsers: boolean = false;
  isRevokingAccess: boolean = false;
  isGrantingAccess: boolean = false;
  isSearchingForNewUser: boolean = false;
  usersLoadError: boolean = false;
  isOrgNotSynced: boolean = false;

  _notificationsStore: INotificationsStore;
  _organizationsStore: IOrganizationsStore;
  _usersService: IUsersService;

  constructor(usersStore: IUsersService, notificationsStore: INotificationsStore, organizationsStore: IOrganizationsStore) {
    super();

    this._notificationsStore = notificationsStore;
    this._organizationsStore = organizationsStore;
    this._usersService = usersStore;

    makeObservable(this, {
      users: observable,
      isLoadingUsers: observable,
      isRevokingAccess: observable,
      isGrantingAccess: observable,
      isSearchingForNewUser: observable,
      usersLoadError: observable,
      isOrgNotSynced: observable,
      selectedUser: observable,
      setSelectedUser: action,
      getUsers: action,
      findNewUserByEmail: action,
      addDirectly: action,
      RevokeUser: action,
    });
  }

  parseUserType(typeString: string | null): SingleUserType['type'] {
    switch (typeString) {
      case 'Coach':
      case 'Admin':
        return typeString;
      default:
        return 'Coach';
    }
  }

  async addDirectly(user: UserDataAndOrganization, organizationId: string) {
    const response = await this._usersService.addToOrganization({ 
      id: user.id, 
      email: user.email, 
      type: this.parseUserType(user.type) 
    }, organizationId);

    if (response.isError) {
      this._notificationsStore.addToast('Failed to add user', getErrorMessage(response.error), 'error');
    }
    else {
      this.getUsers(organizationId);
    }
  }

  async getUsers(orgId: string) {
    try {
      this.setIsLoadingUsers(true);
      this.usersLoadError = false;
      this.isOrgNotSynced = false;

      const response = await this._usersService.getAllUsers(orgId);
      
      if (response?.status === 404) {
        this.isOrgNotSynced = true;
        return;
      }
      
      if (response.data && !response.isError) {
        this.setUsers(response.data);
      }
    } catch (error: any) {
      this.usersLoadError = true;
      this._notificationsStore.addToast('Error', getErrorMessage(error), 'error');
    } finally {
      this.setIsLoadingUsers(false);
    }
  }

  async RevokeUser(content: IRevokeAccess, CB?: () => void) {
    try {
      this.isRevokingAccess = true;

      const response = await this._usersService.revokeUser(content);
      storeResponseHandler(response, t('userHasBeenRevokedAccessSuccessfully'), this._notificationsStore.addToast);

      if (response) {
        this.getUsers(content.organizationId);
        if (CB) CB();
      }
    } catch (error: any) {
      this._notificationsStore.addToast('Error', getErrorMessage(error), 'error');
    } finally {
      this.isRevokingAccess = false;
    }
  }

  async findNewUserByEmail(email: string) {
    try {
      this.isSearchingForNewUser = true;

      const response = await this._usersService.findNewUserByEmail(email);

      if (response.data && !response.isError) {
        return response.data;
      }
    } catch (error: any) {
      this._notificationsStore.addToast('Error', getErrorMessage(error), 'error');
    } finally {
      this.isSearchingForNewUser = false;
    }
  }

  // async getUserRoles() {
  //   try {
  //     this.setIsLoadingUsers(true);

  //     // const response = await this._usersService.getUserRoles();
  //     // console.log('🚀 ~ file: UsersStore.ts:153 ~ UsersStore ~ getUserRoles ~ response:', response);
  //     // if (response?.data?.data && !response.isError) {
  //     //   //
  //     // }
  //   } catch (error: any) {
  //     this._notificationsStore.addToast('Error', getErrorMessage(error), 'error');
  //   } finally {
  //     this.setIsLoadingUsers(false);
  //   }
  // }

  // =========================== UTILS ===========================

  setIsLoadingUsers(status: boolean) {
    this.isLoadingUsers = status;
  }

  setUsers(users: SingleUserType[]) {
    this.users = users;
  }

  setSelectedUser = (user: SingleUserType | null) => {
    this.selectedUser = user;
  };
}
